import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ProductCard from '../common/ProductCard';
import { Product, Category, getProductCategory, formatCategoryLabel } from '../../types';
import { useAdmin } from '../../contexts/AdminContext';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';
import SEOHead from '../common/SEOHead';
import { getCategorySEO, SITE_CONFIG } from '../../utils/seo';
import { getProductPriceInfo } from '../../utils/pricing';

interface ShopProps {
  products?: Product[];
}

const Shop: React.FC<ShopProps> = ({ products: legacyProducts }) => {
  const { products: supabaseProducts, categories: supabaseCategories } = useSupabaseData();
  const { category, subcategory } = useParams<{ category?: string; subcategory?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { flatCategories } = useAdmin();

  const searchQuery = searchParams.get('search') || '';
  const activeCategories = supabaseCategories.length > 0 ? supabaseCategories : flatCategories;

  const products: Product[] = useMemo(() => {
    if (supabaseProducts.length > 0) return supabaseProducts;
    return legacyProducts || [];
  }, [supabaseProducts, legacyProducts]);

  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('popular');
  const [viewMode, setViewMode] = useState<'grid' | 'compact'>('grid');
  const productsPerPage = 20;

  const requestedPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const setCurrentPage = (page: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  const formatCategoryName = formatCategoryLabel;

  const topLevelCategories = useMemo(() => {
    return activeCategories.filter(cat => cat.level === 0);
  }, [activeCategories]);

  // Set of category names that have at least one active product. Used to hide
  // empty sub-categories (e.g. an emptied combo-products) from the sidebar.
  const usedCategoryNames = useMemo(() => {
    const set = new Set<string>();
    for (const p of products) {
      for (const c of p.categories || []) set.add(c);
      const cat = getProductCategory(p);
      if (cat) set.add(cat);
    }
    return set;
  }, [products]);

  const hierarchicalCategories = useMemo(() => {
    // Count products for a single category name (non-recursive — each sub-cat
    // is a leaf, so this is sufficient for sub-category labels).
    const countForCategory = (catName: string) => products.filter(p =>
      (p.categories || []).includes(catName) || getProductCategory(p) === catName
    ).length;

    return topLevelCategories.map(topCat => ({
      ...topCat,
      // Drop sub-categories that have zero products (so an empty combo-products
      // bucket disappears instead of showing "0"). Each child gets its own count.
      // Force-sort by displayOrder so the sidebar/chip bar honor manual ordering
      // even if upstream contexts re-shuffle the array.
      children: activeCategories
        .filter(cat => cat.parentId === topCat.id)
        .filter(sub => usedCategoryNames.has(sub.name))
        .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999) || a.id - b.id)
        .map(sub => ({ ...sub, count: countForCategory(sub.name) })),
      count: products.filter(p => {
        const getDescendantNames = (catId: number): string[] => {
          const descendants = activeCategories.filter(c => c.parentId === catId);
          let allNames = [topCat.name];
          descendants.forEach(desc => { allNames.push(desc.name); allNames.push(...getDescendantNames(desc.id)); });
          return allNames;
        };
        return getDescendantNames(topCat.id).some(catName =>
          (p.categories || []).includes(catName) || getProductCategory(p) === catName
        );
      }).length
    }));
  }, [topLevelCategories, activeCategories, products, usedCategoryNames]);

  const categories = useMemo(() => {
    const baseCategory = { id: 0, name: 'All', level: -1 };
    // For sub-category lists, hide entries that have zero active products
    // (so combo-products doesn't show up after we emptied it).
    const isVisibleSub = (sub: Category) => usedCategoryNames.has(sub.name);

    if (selectedCategory === 'All') {
      return [baseCategory, ...activeCategories.filter(cat => cat.level === 0)];
    }
    const selectedCat = activeCategories.find(cat => cat.name === selectedCategory);
    if (!selectedCat) return [baseCategory, ...activeCategories.filter(cat => cat.level === 0)];
    const byDisplayOrder = (a: Category, b: Category) =>
      (a.displayOrder ?? 999) - (b.displayOrder ?? 999) || a.id - b.id;
    if (selectedCat.level === 0) {
      const children = activeCategories.filter(cat => cat.parentId === selectedCat.id).filter(isVisibleSub).sort(byDisplayOrder);
      return [baseCategory, selectedCat, ...children];
    }
    const parent = activeCategories.find(cat => cat.id === selectedCat.parentId);
    if (parent) {
      const siblings = activeCategories.filter(cat => cat.parentId === parent.id).filter(isVisibleSub).sort(byDisplayOrder);
      return [baseCategory, parent, ...siblings];
    }
    return [baseCategory, ...activeCategories.filter(cat => cat.level === 0)];
  }, [activeCategories, selectedCategory, usedCategoryNames]);

  const isFirstMountRef = useRef(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (searchQuery) {
      setSelectedCategory('Search Results');
    } else if (subcategory) {
      setSelectedCategory(decodeURIComponent(subcategory));
    } else if (category) {
      setSelectedCategory(decodeURIComponent(category));
    } else {
      setSelectedCategory('All');
    }
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
    } else {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory, searchQuery]);

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(categoryName);
    if (categoryName === 'All') { navigate('/shop'); return; }
    let clickedCategory: Category | undefined;
    if (category && subcategory !== categoryName) {
      const currentParentCategory = activeCategories.find(cat => cat.name === category);
      if (currentParentCategory) {
        clickedCategory = activeCategories.find(cat => cat.name === categoryName && cat.parentId === currentParentCategory.id);
      }
    }
    if (!clickedCategory) clickedCategory = activeCategories.find(cat => cat.name === categoryName);
    if (!clickedCategory) { navigate('/shop'); return; }
    if (clickedCategory.level === 0) {
      navigate(`/shop/${encodeURIComponent(categoryName)}`);
    } else {
      const parentCategory = activeCategories.find(cat => cat.id === clickedCategory!.parentId);
      if (parentCategory) {
        navigate(`/shop/${encodeURIComponent(parentCategory.name)}/${encodeURIComponent(categoryName)}`);
      } else {
        navigate(`/shop/${encodeURIComponent(categoryName)}`);
      }
    }
  };

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = products.filter(product => {
        return (
          (product.name && product.name.toLowerCase().includes(query)) ||
          (product.description && product.description.toLowerCase().includes(query)) ||
          (product.ingredients && product.ingredients.toLowerCase().includes(query)) ||
          getProductCategory(product).toLowerCase().includes(query)
        );
      });
    } else if (selectedCategory !== 'All') {
      const selectedCat = activeCategories.find(cat => cat.name === selectedCategory);
      if (selectedCat) {
        const getDescendantNames = (catId: number): string[] => {
          const descendants = activeCategories.filter(cat => cat.parentId === catId);
          let allNames = [selectedCategory];
          descendants.forEach(desc => { allNames.push(desc.name); allNames.push(...getDescendantNames(desc.id)); });
          return allNames;
        };
        const searchCategories = getDescendantNames(selectedCat.id);
        filtered = products.filter(product =>
          searchCategories.some(catName =>
            (product.categories || []).includes(catName) || getProductCategory(product) === catName
          )
        );
      } else {
        filtered = products.filter(product =>
          (product.categories || []).includes(selectedCategory) || getProductCategory(product) === selectedCategory
        );
      }
    }
    switch (sortBy) {
      case 'price_low': return [...filtered].sort((a, b) => (getProductPriceInfo(a).perPill || getProductPriceInfo(a).finalPrice) - (getProductPriceInfo(b).perPill || getProductPriceInfo(b).finalPrice));
      case 'price_high': return [...filtered].sort((a, b) => (getProductPriceInfo(b).perPill || getProductPriceInfo(b).finalPrice) - (getProductPriceInfo(a).perPill || getProductPriceInfo(a).finalPrice));
      case 'rating': return [...filtered].sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'name': return [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      default: {
        const bySortOrder = [...filtered].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
        // Inside weight-management, GLP-1 family (Wegovy/Ozempic/Rybelsus generics) goes
        // to the top — oral first (Rybelsus tabs), then pens. sort_order ties get broken
        // by stable sort, so the 7-product clash at sort_order=10 stops shuffling DPP-4
        // ahead of Sematrinity 1mg.
        if (selectedCategory !== 'weight-management') return bySortOrder;
        const glpRank = (p: Product) => {
          const cats = (p.categories || []) as string[];
          if (cats.includes('rybelsus')) return 0;
          if (cats.includes('wegovy')) return 1;
          if (cats.includes('glp1-agonists')) return 2;
          return 3;
        };
        return bySortOrder.sort((a, b) => glpRank(a) - glpRank(b));
      }
    }
  }, [products, selectedCategory, sortBy, activeCategories, searchQuery]);

  const totalPages = Math.ceil(filteredAndSortedProducts.length / productsPerPage);
  const currentPage = totalPages > 0 ? Math.min(requestedPage, totalPages) : 1;
  const startIndex = (currentPage - 1) * productsPerPage;
  const currentProducts = filteredAndSortedProducts.slice(startIndex, startIndex + productsPerPage);

  // Category showcase: top 10 products per top-level category (used when "All" is selected)
  const isShowcaseMode = selectedCategory === 'All' && !searchQuery;
  const categoryShowcase = useMemo(() => {
    if (!isShowcaseMode) return [];
    return hierarchicalCategories.map(cat => {
      const getDescendantNames = (catId: number): string[] => {
        const descendants = activeCategories.filter(c => c.parentId === catId);
        let allNames = [cat.name];
        descendants.forEach(desc => { allNames.push(desc.name); allNames.push(...getDescendantNames(desc.id)); });
        return allNames;
      };
      const catNames = getDescendantNames(cat.id);
      const catProducts = products
        .filter(p => catNames.some(n => (p.categories || []).includes(n) || getProductCategory(p) === n))
        .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
        .slice(0, 10);
      return { ...cat, products: catProducts };
    }).filter(cat => cat.products.length > 0);
  }, [isShowcaseMode, hierarchicalCategories, products, activeCategories]);

  const shopSEO = useMemo(() => {
    if (category) return getCategorySEO(category, filteredAndSortedProducts);
    if (searchQuery) {
      return {
        title: `"${searchQuery}" Search Results | LiberaCure`,
        description: `Browse products related to ${searchQuery}. Find affordable medications at LiberaCure.`,
        keywords: `${searchQuery}, search, pharmacy, medications`,
        openGraph: { title: `${searchQuery} Search | LiberaCure`, description: `${searchQuery} related products`, image: SITE_CONFIG.image, url: `${SITE_CONFIG.url}/shop?search=${encodeURIComponent(searchQuery)}`, type: 'website' },
        twitter: { card: 'summary_large_image', title: `${searchQuery} Search | LiberaCure`, description: `${searchQuery} results`, image: SITE_CONFIG.image }
      };
    }
    return {
      title: 'Online Pharmacy | Medications & Health Products | LiberaCure',
      description: 'Browse our wide selection of affordable medications and health products.',
      keywords: 'online pharmacy, medications, health products',
      openGraph: { title: 'Online Pharmacy | LiberaCure', description: 'Your Trusted Global Pharmacy', image: SITE_CONFIG.image, url: `${SITE_CONFIG.url}/shop`, type: 'website' },
      twitter: { card: 'summary_large_image', title: 'Online Pharmacy | LiberaCure', description: 'Affordable medications worldwide', image: SITE_CONFIG.image }
    };
  }, [category, searchQuery, filteredAndSortedProducts]);

  return (
    <>
      <SEOHead seoData={shopSEO} />
      <div className="min-h-screen bg-cream-50">
        <div className="flex">

          {/* ===== Left Sidebar — Desktop Category Panel ===== */}
          <aside className="hidden lg:block w-[260px] flex-shrink-0 border-r border-warm-200 min-h-screen sticky top-[72px] self-start">
            <div className="p-8 pt-10">
              <h2 className="font-display text-nano font-semibold text-warm-400 uppercase tracking-[0.15em] mb-6">Categories</h2>

              <nav className="space-y-1">
                <button
                  onClick={() => handleCategoryClick('All')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-[14px] font-medium transition-all duration-200 ${
                    selectedCategory === 'All'
                      ? 'bg-forest-50 text-forest-800'
                      : 'text-warm-600 hover:bg-forest-50/60 hover:text-forest-900'
                  }`}
                >
                  <span className="flex items-center justify-between">
                    All Products
                    <span className="text-micro text-warm-400">{products.length}</span>
                  </span>
                </button>

                {hierarchicalCategories.map((cat) => (
                  <div key={cat.id}>
                    <button
                      onClick={() => handleCategoryClick(cat.name)}
                      className={`w-full text-left px-4 py-3 rounded-xl text-[14px] font-medium transition-all duration-200 ${
                        selectedCategory === cat.name || cat.children.some(sub => sub.name === selectedCategory)
                          ? 'bg-forest-50 text-forest-800'
                          : 'text-warm-600 hover:bg-forest-50/60 hover:text-forest-900'
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        {formatCategoryName(cat.name)}
                        <span className="text-micro text-warm-400">{cat.count}</span>
                      </span>
                    </button>

                    {/* Subcategories — show when parent or any child is selected */}
                    {(selectedCategory === cat.name || cat.children.some(sub => sub.name === selectedCategory)) && cat.children.length > 0 && (
                      <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-forest-200 pl-3">
                        {cat.children.map((sub) => (
                          <button
                            key={sub.id}
                            onClick={() => handleCategoryClick(sub.name)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all duration-200 ${
                              selectedCategory === sub.name
                                ? 'text-forest-800 font-semibold bg-forest-50/50'
                                : 'text-warm-500 hover:text-forest-800 hover:bg-forest-50/50'
                            }`}
                          >
                            <span className="flex items-center justify-between">
                              {formatCategoryName(sub.name)}
                              <span className="text-micro text-warm-400">{sub.count}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </aside>

          {/* ===== Main Content ===== */}
          <main className="flex-1 min-w-0">

            {/* Floating Header Bar */}
            <div className="sticky top-[72px] z-20 bg-cream-50/80 backdrop-blur-2xl border-b border-warm-200">
              <div className="px-6 sm:px-8 lg:px-10">

                {/* Mobile category scroll */}
                <div className="lg:hidden py-4 -mx-6 px-6">
                  <div className="flex overflow-x-auto scrollbar-hide gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryClick(cat.name)}
                        className={`whitespace-nowrap px-4 py-2 rounded-full text-[13px] font-medium transition-all duration-200 ${
                          selectedCategory === cat.name
                            ? 'bg-forest-800 text-cream-100'
                            : 'bg-warm-100 text-warm-600 hover:bg-warm-200'
                        } ${cat.level > 0 ? 'text-[12px]' : ''}`}
                      >
                        {formatCategoryName(cat.name)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title + Controls bar */}
                <div className="flex items-center justify-between py-5 lg:py-6">
                  <div className="flex items-center gap-4">
                    <h1 className="font-display text-heading lg:text-heading-lg text-forest-900 tracking-tight">
                      {searchQuery ? `"${searchQuery}"` : selectedCategory === 'All' ? 'All Products' : formatCategoryName(selectedCategory)}
                    </h1>
                    <span className="text-caption text-warm-400 bg-warm-100 px-3 py-1 rounded-full">
                      {filteredAndSortedProducts.length}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* View toggle */}
                    <div className="hidden sm:flex items-center bg-warm-100 rounded-full p-1">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-1.5 rounded-full transition-all duration-200 ${viewMode === 'grid' ? 'bg-cream-50 shadow-xs text-forest-900' : 'text-warm-400'}`}
                        aria-label="Grid view"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setViewMode('compact')}
                        className={`p-1.5 rounded-full transition-all duration-200 ${viewMode === 'compact' ? 'bg-cream-50 shadow-xs text-forest-900' : 'text-warm-400'}`}
                        aria-label="Compact view"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
                        </svg>
                      </button>
                    </div>

                    {/* Sort */}
                    <select
                      value={sortBy}
                      onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                      className="text-caption border border-warm-200 rounded-full px-4 py-2 text-forest-800 bg-cream-50 focus:outline-none focus:ring-2 focus:ring-forest-200 hover:border-warm-300 transition-colors cursor-pointer"
                    >
                      <option value="popular">Popular</option>
                      <option value="price_low">Price: Low to High</option>
                      <option value="price_high">Price: High to Low</option>
                      <option value="rating">Highest Rated</option>
                      <option value="name">Name: A-Z</option>
                    </select>
                  </div>
                </div>

                {/* Search indicator */}
                {searchQuery && (
                  <div className="pb-4 -mt-2">
                    <button
                      onClick={() => navigate('/shop')}
                      className="inline-flex items-center gap-2 text-caption text-forest-700 bg-forest-50 px-4 py-1.5 rounded-full hover:bg-forest-100 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Clear search
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Products */}
            <div className="px-6 sm:px-8 lg:px-10 py-8 lg:py-10">

              {/* ═══ Category Showcase (when "All Products" selected) ═══ */}
              {isShowcaseMode ? (
                <div className="space-y-14">
                  {categoryShowcase.map((cat) => (
                    <section key={cat.id}>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <h2 className="font-display text-xl lg:text-2xl text-forest-900 tracking-tight">
                            {formatCategoryLabel(cat.name)}
                          </h2>
                          <span className="text-xs text-warm-400 bg-warm-100 px-2.5 py-0.5 rounded-full">
                            {cat.count}
                          </span>
                        </div>
                        <Link
                          to={`/shop/${encodeURIComponent(cat.name)}`}
                          className="text-sm font-medium text-forest-600 hover:text-forest-800 transition-colors flex items-center gap-1"
                        >
                          View All
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                          </svg>
                        </Link>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5">
                        {cat.products.map((product) => {
                          const priceInfo = getProductPriceInfo(product);
                          return (
                            <ProductCard
                              key={product.id}
                              id={product.id}
                              category={getProductCategory(product)}
                              name={product.name || ''}
                              originalPrice={priceInfo.originalPrice}
                              discount={priceInfo.discount}
                              finalPrice={priceInfo.finalPrice}
                              perPill={priceInfo.perPill}
                              quantity={priceInfo.quantity}
                              manufacturer={product.manufacturer || ''}
                              rating={product.rating || 0}
                              reviewCount={product.review_count || 0}
                              image={product.image || undefined}
                            />
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <>
                  {/* ═══ Normal Grid (category/search selected) ═══ */}
                  <div className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-7'
                      : 'grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5'
                  }>
                    {currentProducts.map((product) => {
                      const priceInfo = getProductPriceInfo(product);
                      return (
                        <ProductCard
                          key={product.id}
                          id={product.id}
                          category={getProductCategory(product)}
                          name={product.name || ''}
                          originalPrice={priceInfo.originalPrice}
                          discount={priceInfo.discount}
                          finalPrice={priceInfo.finalPrice}
                          perPill={priceInfo.perPill}
                          quantity={priceInfo.quantity}
                          manufacturer={product.manufacturer || ''}
                          rating={product.rating || 0}
                          reviewCount={product.review_count || 0}
                          image={product.image || undefined}
                        />
                      );
                    })}
                  </div>

                  {/* Empty state */}
                  {currentProducts.length === 0 && (
                    <div className="text-center py-24">
                      <div className="w-16 h-16 bg-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <svg className="w-8 h-8 text-warm-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                      </div>
                      <h3 className="font-display text-heading text-forest-900 mb-2">No products found</h3>
                      <p className="text-body-sm text-warm-500 mb-6">Try adjusting your search or browse all products.</p>
                      <button onClick={() => navigate('/shop')} className="btn-primary text-sm px-6 py-3">Browse All</button>
                    </div>
                  )}
                </>
              )}

              {/* Pagination (only for normal grid, not showcase) */}
              {!isShowcaseMode && totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-14">
                  <button
                    onClick={() => { setCurrentPage(Math.max(1, currentPage - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={currentPage === 1}
                    className="px-5 py-2.5 rounded-full border border-warm-200 text-caption font-medium text-warm-600 bg-cream-50 hover:bg-cream-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                      return (
                        <button
                          key={page}
                          onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                          className={`w-10 h-10 rounded-full text-caption font-medium transition-all duration-200 ${
                            currentPage === page
                              ? 'bg-forest-800 text-cream-100'
                              : 'border border-warm-200 text-warm-600 bg-cream-50 hover:bg-cream-100'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                      return <span key={page} className="px-1.5 text-warm-400">...</span>;
                    }
                    return null;
                  })}

                  <button
                    onClick={() => { setCurrentPage(Math.min(totalPages, currentPage + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={currentPage === totalPages}
                    className="px-5 py-2.5 rounded-full border border-warm-200 text-caption font-medium text-warm-600 bg-cream-50 hover:bg-cream-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default Shop;
